import Button from '@ingka/button';
import Loading, { LoadingBall } from '@ingka/loading';
import information from '@ingka/ssr-icon/paths/information';
import Tooltip from '@ingka/tooltip';
import type { LotteryEditPhase } from '@lotta/feature-lottery-rules';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { DateTime } from 'luxon';
import { memo, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../LotteryForm.module.scss';
import { ControlledDateInput } from '../../components/controlled-date-input/ControlledDateInput';
import { ControlledDrawTimeDropdown } from '../../components/controlled-draw-time-dropdown/ControlledDrawTimeDropdown';
import { ControlledDropdown } from '../../components/controlled-dropdown/ControlledDropdown';
import { ControlledQuantityStepper } from '../../components/controlled-quantity-stepper/ControlledQuantityStepper';
import { LotteryFormContext, allowFieldInput } from '../../lottery-form-context';
import { getDaysOfWeek } from '../../utils/get-days-of-week';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../types';
import { WeeklyDrawDaysPreview } from './draw-days-preview/WeeklyDrawDaysPreview';
import { fromPayload } from './from-payload';
import { toPayload } from './to-payload';

export const MODULE_NAME = 'weekly-occurrence';

export type WeeklyOccurrenceFormType = Static<typeof WeeklyOccurrenceFormSchema>;
export const WeeklyOccurrenceFormSchema = Type.Object(
  {
    // occurrenceType: Type.Literal('WEEKLY'),
    startDate: Type.String({
      format: 'date',
      formatMinimum: DateTime.now().toISODate(),
      errorMessage: {
        format: 'form-error-message-date-invalid-format',
        formatMinimum: 'form-start-month-error-message-minimum',
        formatMaximum: 'form-start-month-error-message-maximum',
      },
    }),
    numberOfWeeks: Type.Integer({ minimum: 1, maximum: 200 }),
    drawDay: Type.Integer({
      minimum: 1,
      errorMessage: {
        type: 'form-error-message-minimum-day-value',
        minimum: 'form-error-message-minimum-day-value',
      },
    }), // 1-7 is MON-SUN
    drawTime: Type.RegEx(/^(0[0-9]|1[0-9]|2[0-3]):00$/, {
      errorMessage: {
        pattern: 'form-error-message-required',
      },
    }),
  },
  { additionalProperties: false, $id: 'WeeklyOccurrenceFormSchema' }
);

const editEnabledConfig: Record<keyof WeeklyOccurrenceFormType, LotteryEditPhase[]> = {
  startDate: ['OPEN', 'COPY'],
  numberOfWeeks: ['OPEN', 'LIMITED', 'COPY'],
  drawDay: ['OPEN', 'LIMITED', 'COPY'],
  drawTime: ['OPEN', 'LIMITED', 'COPY'],
};

const getSchema = (initialValues: WeeklyOccurrenceFormType | undefined) => {
  if (!initialValues) {
    return WeeklyOccurrenceFormSchema;
  }
  // For edit lottery, modify validation for startDate to allow leaving it unchanged
  return Type.Object(
    {
      ...WeeklyOccurrenceFormSchema.properties,
      startDate: Type.String({
        ...WeeklyOccurrenceFormSchema.properties.startDate,
        formatMinimum: DateTime.min(
          DateTime.fromISO(initialValues.startDate),
          DateTime.now()
        ).toISODate(),
      }),
    },
    { additionalProperties: false, $id: 'UpdateWeeklyOccurrenceFormSchema' }
  );
};

export const WeeklyOccurrenceFormTransformer: ModuleTransformer<WeeklyOccurrenceFormType> = {
  fromPayload,
  toPayload,
};

const defaultValues: WeeklyOccurrenceFormType = {
  startDate: '',
  numberOfWeeks: 1,
  drawDay: 0,
  drawTime: '',
};

export type WeeklyOccurrenceFormProps = FormModuleProps<WeeklyOccurrenceFormType>;

export const WeeklyOccurrenceForm: React.FC<WeeklyOccurrenceFormProps> = memo(
  ({ initialValues, retailUnit, values, onChange, onValidation }) => {
    const lotteryFormContext = useContext(LotteryFormContext);
    const { data: retailUnitData, isLoading: retailUnitIsLoading } = useGetRetailUnitQuery({
      retailUnit,
    });
    const { t } = useTranslation();
    const { locale } = useLocale();

    const schema = useMemo(() => getSchema(initialValues), [initialValues]);

    const {
      control,
      formState: { errors },
    } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema,
      onChange,
      onValidation,
    });

    // Name of the week days in current locale, including start of week day
    const drawDayOptions = useMemo(() => {
      return getDaysOfWeek(locale, 'long').map(({ day, name }) => ({
        name,
        value: `${day}`,
      }));
    }, [locale]);

    if (retailUnitIsLoading) {
      return (
        <Loading text={`${t('general-fetching-template')}`}>
          <LoadingBall />
        </Loading>
      );
    }

    const timeZone = retailUnitData?.retailUnit.timeZone ?? '';

    return (
      <form>
        <div className={styles['form-elements']}>
          <ControlledDateInput
            name="startDate"
            control={control}
            disabled={!allowFieldInput(editEnabledConfig.startDate, lotteryFormContext)}
            locale={locale}
            labelKey="general-start-date"
          />
        </div>
        <div className={styles['form-elements']}>
          <ControlledQuantityStepper
            name="numberOfWeeks"
            control={control}
            disabled={!allowFieldInput(editEnabledConfig.numberOfWeeks, lotteryFormContext)}
            label={`${t('general-number-of-weeks')}`}
            minVal={WeeklyOccurrenceFormSchema.properties.numberOfWeeks.minimum ?? 1}
            maxVal={WeeklyOccurrenceFormSchema.properties.numberOfWeeks.maximum ?? 52}
          />
        </div>
        <div className={styles['form-elements']}>
          <ControlledDropdown
            name="drawDay"
            control={control}
            disabled={!allowFieldInput(editEnabledConfig.drawDay, lotteryFormContext)}
            id="lotta-weekly-draw-day-of-week-dropdown"
            label={`${t('lottery-form-draw-day-of-week')}`}
            hintText={`${t('lottery-form-select-draw-day-of-week')}`}
            errorMessage={errors.drawDay?.message as string}
            options={drawDayOptions}
          />
        </div>
        <div className={styles['tool-tip-container']}>
          <div className={styles['form-elements']}>
            <ControlledDrawTimeDropdown
              name="drawTime"
              control={control}
              disabled={!allowFieldInput(editEnabledConfig.drawTime, lotteryFormContext)}
              errorMessage={errors.drawTime?.message as string}
              timeZone={timeZone}
            />
          </div>
          <div className={styles['tool-tip']}>
            <Tooltip
              tooltipText={t('lottery-form-draw-time-dropdown-tooltip')}
              ssrIcon={information}
              position="trailing"
            >
              <Button iconOnly ssrIcon={information} type="tertiary" small />
            </Tooltip>
          </div>
        </div>
        <WeeklyDrawDaysPreview control={control} timezone={timeZone} />
      </form>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: WeeklyOccurrenceFormTransformer,
  form: WeeklyOccurrenceForm,
} as const;
